import { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { PaymentRequest, PaymentRequestPaymentMethodEvent, Stripe } from '@stripe/stripe-js';
import { trackPaymentGateway } from '../../analytics';
import { EVENTS as E, PAYMENT_GATEWAY_PROPERTY, PRODUCT_NAME } from '../constants';
import { selectPlanCoupon, selectPlanDetails } from '../store/plans';
import { stripeMinorUnitsFromMajorAmount } from '../utils';
import { useSendEvents } from './useSendEvents';
import { useStripeAccount } from './useStripeAccount';
import type { PlanEventDetails, StripePurchaseClick } from './useStripePurchase';

interface UseWalletPaymentParams {
  screenId: string;
  stripeInstance: Stripe | null;
  stripePurchaseClick: StripePurchaseClick;
  getPlanDetails: () => PlanEventDetails;
}

export const useWalletPayment = ({
  screenId,
  stripeInstance,
  stripePurchaseClick,
  getPlanDetails,
}: UseWalletPaymentParams) => {
  const dispatch = useDispatch();
  const sendEvents = useSendEvents({ screenId });
  const planDetails = useSelector(selectPlanDetails);
  const couponDetails = useSelector(selectPlanCoupon);
  const { stripeAccountName, stripeGateway } = useStripeAccount();

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [wallet, setWallet] = useState<string | undefined>();

  const walletButtonClick = () => {
    sendEvents(E.BUY_SUBSCRIPTION_TAPPED, {
      payment_method: wallet?.toLowerCase() || 'wallet',
      payment_processor: 'Stripe',
      stripe_account_name: stripeAccountName,
      [PAYMENT_GATEWAY_PROPERTY]: trackPaymentGateway(dispatch, stripeGateway),
      ...getPlanDetails(),
    });
  };

  const onWalletPaymentMethod = useRef<(e: PaymentRequestPaymentMethodEvent) => void>();
  onWalletPaymentMethod.current = (e) => {
    sendEvents(E.PROCESSING_STARTED, {
      payment_method: e.walletName?.toLowerCase() || wallet?.toLowerCase() || 'wallet',
      payment_processor: 'Stripe',
      stripe_account_name: stripeAccountName,
      [PAYMENT_GATEWAY_PROPERTY]: trackPaymentGateway(dispatch, stripeGateway),
      ...getPlanDetails(),
    });
    stripePurchaseClick(
      e,
      () => e.complete('success'),
      () => e.complete('fail'),
    );
  };

  useLayoutEffect(() => {
    if (!stripeInstance || !planDetails) return;

    setPaymentRequest(null);
    const label = `${PRODUCT_NAME} ${planDetails.description} plan`;
    const majorTotal = +(
      (planDetails.periodPrice || 0) - (couponDetails?.couponPrice || 0)
    ).toFixed(2);
    const amount = stripeMinorUnitsFromMajorAmount(majorTotal, planDetails.currency);
    if (isNaN(amount) || amount <= 0) {
      setWallet(undefined);
      return;
    }

    const pr = stripeInstance.paymentRequest({
      country: 'US',
      currency: (planDetails.currency || 'USD').toLowerCase(),
      total: { label, amount },
      displayItems: [{ label, amount }],
      requestPayerName: true,
      requestPayerEmail: true,
    });

    let cancelled = false;

    pr.canMakePayment().then((result: any) => {
      if (cancelled) return;
      if (!result) {
        setWallet(undefined);
        return;
      }
      const availableWallet = Object.keys(result).find(
        (key) => ['applePay', 'googlePay'].includes(key) && result[key],
      );
      setWallet(availableWallet);
      setPaymentRequest(pr);
    });

    pr.on('paymentmethod', (e) => onWalletPaymentMethod.current?.(e));

    return () => {
      cancelled = true;
      pr.off('paymentmethod');
    };
  }, [stripeInstance, planDetails, couponDetails]);

  return { paymentRequest, wallet, walletButtonClick };
};
